import type { VideoUploaderFormFields } from ".";
import { useFormContext } from "react-hook-form";
import { useMutation } from "react-query";
import type { ApiError } from "@/react/config/CustomErrors";
import { reactQueryPost } from "@/react/helpers/backendRequestHelpers";
import { activeStorageBlobUrl, internalApi } from "@/react/helpers/urlHelpers";
import {
  useAttachmentMetadata,
  useAttachments,
} from "@circle-react-shared/BuilderUI/Media/AttachmentContext";
import type { InlineAttachment } from "./MediaPicker/useMediaPicker";

export interface EmbedResponse {
  html: string;
}
export interface VideoUploaderProps {
  name: string;
}

export const useVideoUploader = ({ name }: VideoUploaderProps) => {
  const { watch, setValue } = useFormContext<VideoUploaderFormFields>();
  const { addLocalAttachment } = useAttachments();

  const signed_id = watch(`${name}.signed_id`, "");
  const customCoverImage = watch(`${name}.customCoverImage`);

  const attachmentMetadata = useAttachmentMetadata(signed_id);
  const customCoverAttachmentMetadata = useAttachmentMetadata(
    customCoverImage?.signed_id || "",
  );

  const { url = "", filename = "" } = attachmentMetadata || {};
  const { url: customCoverImageUrl } = customCoverAttachmentMetadata || {};

  const createEmbedMutation = useMutation<EmbedResponse, ApiError, string>(
    embedUrl =>
      reactQueryPost(
        internalApi.embeds.create({ params: { content: embedUrl } }),
      ),
    {
      onSuccess: ({ html }) => {
        setValue(`${name}.html`, html);
      },
    },
  );

  const handleOnUploadStarted = () => {
    setValue(`${name}.signed_id`, "");
    setValue(`${name}.html`, "");
  };

  const handleOnUploadComplete = (file: InlineAttachment) => {
    const { signed_id = "", filename = "" } = file;

    const url = activeStorageBlobUrl({
      signed_id,
      filename,
    });

    setValue(`${name}.signed_id`, signed_id);
    addLocalAttachment({
      signed_id,
      url,
      filename,
    });
  };

  return {
    signed_id,
    customCoverImageUrl,
    url,
    filename,
    createEmbedMutation,
    handleOnUploadStarted,
    handleOnUploadComplete,
  };
};
